// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import axios from 'axios';
import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = (marketplaceRootURL) => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const rootUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running on a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Safely handle the case when window is not defined
  if (typeof window === 'undefined') {
    // Fallback to marketplaceRootURL or an empty string
    return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : rootUrl;
  }

  // Use the given marketplaceRootURL or the same domain and port as the frontend
  return marketplaceRootURL
    ? marketplaceRootURL.replace(/\/$/, '')
    : `${window.location.origin}`;
};

/**
 * Where this process should fetch its own API from (ticket 94).
 *
 * While rendering a page the server used to fetch its own API over the public address: out
 * through the edge, back through the router, into the same small pool of dynos that was busy
 * rendering the page. That is a whole network round trip per page, it can be served by a dyno
 * that is itself waiting on a render, and it is what made a request hang rather than run
 * slowly.
 *
 * Rendering is the only time this applies. In a browser there is nothing to short-circuit: the
 * page is already talking to the site over the network.
 *
 * **The address cannot be read from the environment here**, which an earlier version of this
 * tried to do. This file does not run as plain Node on the server: it runs from the webpack
 * server bundle, and that build replaces `process.env` as a whole with a literal object holding
 * only NODE_ENV, PUBLIC_URL and the REACT_APP_* values that existed at build time. PORT is
 * assigned per dyno at run time and is not one of them, so reading it here gives undefined in
 * every deployed process while every test passes, because Jest loads this file as source with
 * the real environment.
 *
 * So server/index.js, which is plain Node, works the address out and publishes it on a global
 * before the server takes requests. The bundler does not rewrite `globalThis`. The code that
 * decides the address is server/api-util/internalApiBase.js, which also owns this name.
 *
 * Nothing published means there is no local server to talk to, so this returns null and callers
 * fall back to the public address: a script, the cron worker, or a test run outside the server.
 */
const internalApiBaseUrl = () => {
  const published =
    typeof globalThis === 'undefined' ? null : globalThis.__RAS_INTERNAL_API_BASE__;
  return published ? String(published).replace(/\/$/, '') : null;
};

/** True when this call is a page being rendered and a local server exists to answer it. */
export const isInternalApiCall = () =>
  typeof window === 'undefined' && internalApiBaseUrl() != null;

/**
 * The base address for fetching our own API.
 *
 * Deliberately separate from apiBaseUrl(), which is also used to build addresses that are
 * written into the page or handed to another service: the web manifest link, and the address
 * sign-in returns to. Those must stay the public address whatever this does.
 */
export const apiFetchBaseUrl = () =>
  isInternalApiCall() ? internalApiBaseUrl() : apiBaseUrl();

/**
 * Headers the edge would have set, for the hop it is no longer making.
 *
 * The app redirects anything it sees as plain HTTP (enforceSsl in server/index.js). The
 * loopback hop really is plain HTTP, so without this every internal call would be answered
 * with a redirect back out to the public address, which is the thing being removed.
 */
export const internalApiHeaders = () =>
  isInternalApiCall() ? { 'X-Forwarded-Proto': 'https' } : {};


// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, {
    typeHandlers,
    verbose: appSettings.sdk.transitVerbose,
  });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// Typed error callers can distinguish from network/server failures.
export class ApiTimeoutError extends Error {
  constructor(timeoutMs, path) {
    super(`Request to ${path} timed out after ${timeoutMs}ms`);
    this.name = 'ApiTimeoutError';
    this.timeoutMs = timeoutMs;
    this.path = path;
  }
}

// During SSR, the headers that tag a call to our own /api as coming from the
// server render (X-RAS-Caller: ssr, plus the page and its request id), for the
// perf logs. server/perf installs the hook only when PERF_LOG=true; in the
// browser this returns null before touching globalThis.
const ssrPerfHeaders = () => {
  if (typeof window !== 'undefined') return null;
  try {
    const hook = typeof globalThis !== 'undefined' ? globalThis.__rasPerfSsrHeaders : null;
    return typeof hook === 'function' ? hook() : null;
  } catch (e) {
    return null;
  }
};

// During SSR, a handler server/ssrInProcessApi.js can run in this process, so the render does
// not call itself through the public address. Null in the browser, before touching globalThis,
// and for any call with its own headers.
const ssrInProcessApi = (method, path, headers) => {
  if (typeof window !== 'undefined' || headers) return null;
  try {
    const api = typeof globalThis !== 'undefined' ? globalThis.__rasSsrApi : null;
    return api && typeof api.has === 'function' && api.has(method, path) ? api : null;
  } catch (e) {
    return null;
  }
};

// The in-process call, answered the way the fetch below would answer it: the parsed body,
// an Error carrying the body and httpStatus from 400 up, ApiTimeoutError past timeoutMs.
const requestInProcess = (api, method, path, body, timeoutMs) => {
  const call = api.call(method, path, body).then(({ status, contentType, body: data }) => {
    if (status >= 400) {
      let e = new Error();
      e = Object.assign(e, data);
      e.httpStatus = status;
      throw e;
    }
    return contentType === 'application/json' || typeof data !== 'string'
      ? data
      : String(data);
  });
  if (timeoutMs == null) return call;

  let timeoutId = null;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new ApiTimeoutError(timeoutMs, path)), timeoutMs);
  });
  return Promise.race([call, timeout]).finally(() => clearTimeout(timeoutId));
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const { credentials, headers, body, timeoutMs, ...rest } = options;

  const inProcess = ssrInProcessApi(rest.method, path, headers);
  if (inProcess) {
    return requestInProcess(inProcess, rest.method, path, body, timeoutMs);
  }

  const url = `${apiFetchBaseUrl()}${path}`;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const controller = timeoutMs != null ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  const baseHeaders = headers || { 'Content-Type': 'application/transit+json' };
  // Plain objects only: spreading a Headers instance would drop its entries.
  const addedHeaders =
    Object.getPrototypeOf(baseHeaders) === Object.prototype
      ? { ...ssrPerfHeaders(), ...internalApiHeaders() }
      : null;

  const fetchOptions = {
    credentials: credentials || 'include',
    headers: addedHeaders ? { ...baseHeaders, ...addedHeaders } : baseHeaders,
    ...bodyMaybe,
    ...rest,
    ...(controller ? { signal: controller.signal } : {}),
  };

  // Use universal fetch: browser window.fetch or Node's global.fetch
  const universalFetch = (typeof window !== 'undefined' && window.fetch)
    ? window.fetch.bind(window)
    : (typeof fetch === 'function' ? fetch : undefined);

  if (!universalFetch) {
    throw new Error('Fetch API is not available in this environment');
  }

  return universalFetch(url, fetchOptions)
    .then(res => {
      if (timeoutId) clearTimeout(timeoutId);
      const contentTypeHeader = res.headers.get('Content-Type');
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

      if (res.status >= 400) {
        return res.json().then(data => {
          let e = new Error();
          e = Object.assign(e, data);
          e.httpStatus = res.status;
          throw e;
        });
      }
      if (contentType === 'application/transit+json') {
        return res.text().then(deserialize);
      } else if (contentType === 'application/json') {
        return res.json();
      }
      return res.text();
    })
    .catch(err => {
      if (timeoutId) clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        throw new ApiTimeoutError(timeoutMs, path);
      }
      throw err;
    });
};

// Retry delays for GET requests: 1 s, 2 s, 4 s (3 attempts total).
// Exported so tests can override without fake timers.
export const _retryDelaysMs = [1000, 2000, 4000];

const RETRY_STATUSES = new Set([502, 503, 504]);

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const isRetryableError = err =>
  err instanceof ApiTimeoutError ||
  err instanceof TypeError ||
  (err.httpStatus != null && RETRY_STATUSES.has(err.httpStatus));

// Wraps request() with exponential-backoff retry — GET only (idempotent).
const withGetRetry = (path, options) => {
  const attempt = (retriesLeft, delayIdx) =>
    request(path, options).catch(err => {
      if (retriesLeft > 0 && isRetryableError(err)) {
        return sleep(_retryDelaysMs[delayIdx]).then(() =>
          attempt(retriesLeft - 1, delayIdx + 1)
        );
      }
      throw err;
    });
  return attempt(2, 0); // up to 2 retries → 3 attempts total
};


// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
export const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

export const get = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
  };
  return withGetRetry(path, requestOptions);
};

/**
 * POST with an explicit timeout for long-running endpoints. Sends body as JSON (not transit).
 * Throws ApiTimeoutError on timeout so callers can distinguish it.
 */
export const postWithTimeout = (path, body, timeoutMs) => {
  const apiBase = apiFetchBaseUrl();
  const url = `${apiBase}${path}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const universalFetch = (typeof window !== 'undefined' && window.fetch)
    ? window.fetch.bind(window)
    : (typeof fetch === 'function' ? fetch : undefined);

  const fetchOptions = {
    method: methods.POST,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...internalApiHeaders() },
    body: JSON.stringify(body),
    signal: controller.signal,
  };

  return universalFetch(url, fetchOptions)
    .then(res => {
      clearTimeout(timeoutId);
      const contentTypeHeader = res.headers.get('Content-Type');
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

      if (res.status >= 400) {
        return res.json().then(data => {
          const e = new Error(data.message || data.error || 'Request failed');
          Object.assign(e, data);
          throw e;
        });
      }

      if (contentType === 'application/json') {
        return res.json();
      }
      return res.text();
    })
    .catch(err => {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        throw new ApiTimeoutError(timeoutMs, path);
      }
      throw err;
    });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Authoritative Stripe PaymentIntent status for a transaction. Used on the
// order page to tell whether a "payment confirmation pending" booking has money
// genuinely held (safe to finalize) or the customer still needs to pay.
// Returns { status, category: 'held'|'needs_action'|'failed'|'unknown', lastPaymentErrorCode }.
export const transactionPaymentStatus = transactionId => {
  return post('/api/transaction-payment-status', { transactionId });
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const getExchangeRates = () => {
  return post('/api/stored-exchange-rates', {});
};

export const userActiveListings = userId => {
  return post('/api/current-user-listings-count/published', { userId });
};

export const currentUserListingsClosed = userId => {
  return post('/api/current-user-listings-count/closed', { userId });
};

export const currentUserTransactionsTotals = () => {
  return post('/api/current-user-transactions-total', {});
};

// Algolia
export const createAlgoliaData = body => {
  return post('/api/createAlgoliaData', body);
};

// export const deleteUserAccount = body => {
//   return post('/api/delete-user', body);
// };

export const updateAlgoliaData = body => {
  return post('/api/updateAlgoliaData', body)
}

export const deleteAlgoliaData = body => {
  return post('/api/deleteAlgoliaData', body);
};

export const updateAlgoliaViews = ({ indexName, recordIds }) => {
  return post('/api/updateAlgoliaViews', { indexName, recordIds })
};

export const updaeAlgoliaListingsUser = (userId) => {
  return post('/api/updaeAlgoliaListingsUser', { userId })
}

// OneSignal
export const onSendPushNotification = body => {
  return post('/api/one-signal-push-notification', body);
};

export const syncBadgeCount = body => {
  return post('/api/sync-badge-count', body);
};


// Cloudinary

export const deleteImage = ({ publicId, resourceType = 'image' }) => {
  // console.log('public', publicId);
  return post('/api/deleteImage', { publicId, resourceType });
};

// get user details for generating reviews
export const getUserAchievements = (userId) => {
  return post('/api/user-achievements', { userId });
};

// Delete user account
export const deleteUser = (currentPassword) => {
  return post('/api/delete-user', currentPassword);
};

export const replicateListing = ({ listingId, title, description, currentUserId }) => {
  return post('/api/replicate-listing', { listingId, title, description, currentUserId })
};

export const fetchAlgoliaHits = (data) => {
  return post('/api/fetch-algolia-hits', data)
}

export const sendEmailToSearchTeam = (data) => {
  return post('/api/send-email-to-search-team', data)
}
// Additional notification email addresses (RAS-2647). Add and remove save immediately
// rather than through the Contact details form submit, which is entangled with the
// password-confirm flow for changing the primary email.
export const addNotificationEmailApi = email => {
  return post('/api/notification-emails/add', { email });
};
export const removeNotificationEmailApi = email => {
  return post('/api/notification-emails/remove', { email });
};

// Send Grid mails
export const sendSchedularMailFromSendGrid = (data) => {
  return post('/api/send-schedular-mail', data)
}
export const cancelUpcomingMail = (data) => {
  return post('/api/cancel-upcoming-mail', data)
}

export const getCloudinarySignature = ({ public_id, transformation, folder }) => {
  return post('/api/generate-signature', { public_id, transformation, folder })
}

/**
 * Write SEO context metadata (alt, title, caption, seo_name) to a Cloudinary asset.
 *
 * Fire-and-forget: callers MUST attach their own `.catch()` — this function does
 * not swallow errors and must never block the upload/save flow it is called from.
 *
 * @param {Object}  params
 * @param {string}  params.publicId         - Cloudinary asset public_id (required)
 * @param {Object}  params.context          - SEO fields to attach to the asset
 * @param {string}  params.context.altText  - Alt text (maps to Cloudinary `alt`)
 * @param {string}  params.context.titleTag - Title (maps to Cloudinary `title`)
 * @param {string}  params.context.caption  - Caption (maps to Cloudinary `caption`)
 * @param {string}  params.context.fileName - SEO filename slug (maps to Cloudinary `seo_name`)
 * @param {'image'|'video'} [params.resourceType='image'] - Cloudinary resource type
 * @returns {Promise<{ success: true }>}
 */
export const updateCloudinaryContext = ({ publicId, context, resourceType = 'image' }) => {
  return post('/api/cloudinary/update-context', { publicId, context, resourceType });
};

const QUERY_ALGOLIA_TIMEOUT_MS = 10000;
const QUERY_ALGOLIA_MAX_ATTEMPTS = 3;

export const queryAlgolia = (body) => {
  const apiBase = apiFetchBaseUrl();
  const url = `${apiBase}/api/query-algolia`;

  // A bare axios.post with no timeout can hang indefinitely on flaky / in-app
  // browsers and only rejects long after the user has given up. Bound each
  // attempt, and retry transient failures (timeout / no response / 5xx) with
  // exponential backoff. Once all attempts are exhausted the rejection is
  // surfaced to the caller as a hard failure — there is no user-facing retry.
  const attempt = n =>
    axios
      .post(url, body, {
        headers: { 'Content-Type': 'application/json', ...internalApiHeaders() },
        withCredentials: true,
        timeout: QUERY_ALGOLIA_TIMEOUT_MS,
      })
      .then(response => response.data)
      .catch(err => {
        const status = err?.response?.status;
        const isRetriable = err?.code === 'ECONNABORTED' || !status || status >= 500;
        if (n < QUERY_ALGOLIA_MAX_ATTEMPTS && isRetriable) {
          const backoffMs = 400 * 2 ** (n - 1); // 400ms, then 800ms
          return new Promise(resolve => setTimeout(resolve, backoffMs)).then(() => attempt(n + 1));
        }
        throw err;
      });

  return attempt(1);
}

export const fetchUserCountry = async () => {
  const response = await fetch('https://ip.rentanythingstore.com');
  const vr1 = await response.text();
  const match = vr1.match(/User Country Code: (\w+)/);
  return match ? match[1] : null;
};

export const getLocationWithMapBox = async (longitude, latitude) => {
  const response = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}`
  );
  return await response.json();
}

export const boostAvailability = (body) => {
  return post('/api/update-listing-availability', body);
}

// Micro-Service API Calls
const microServiceBaseURL = process.env.REACT_APP_MICROSERVICE_BASE_URL || 'https://rasmicroservice.onrender.com'
const axiosMicroService = axios.create({
  baseURL: microServiceBaseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

const getMicroserviceBaseUrl = () => microServiceBaseURL;

/**
 * Fire-and-forget blog view/click update using fetch + keepalive (does not block first paint).
 * Client-only; no-op on server. Same payload shape as updateBlogPost / updateStoreBlogsPost.
 */
export const updateBlogPostViewsKeepalive = (id, update) => {
  if (typeof window === 'undefined') return;
  const url = `${getMicroserviceBaseUrl()}/blogs/${id}`;
  requestAnimationFrame(() => {
    fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
      credentials: 'include',
      keepalive: true,
    }).catch(() => {});
  });
};

export const updateStoreBlogViewsKeepalive = (id, update) => {
  if (typeof window === 'undefined') return;
  const url = `${getMicroserviceBaseUrl()}/store-blogs/${id}`;
  requestAnimationFrame(() => {
    fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
      credentials: 'include',
      keepalive: true,
    }).catch(() => {});
  });
};

// Listing Generation API's
export const generateAIListing = (body, imageFiles) => {
  const formData = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(v => formData.append(key, v));
    } else {
      formData.append(key, value);
    }
  });

  // Only append images if present and not empty
  if (imageFiles && imageFiles.length > 0) {
    if (Array.isArray(imageFiles)) {
      imageFiles.forEach(file => {
        formData.append('images', file);
      });
    } else {
      formData.append('images', imageFiles);
    }
  }

  return axiosMicroService.post('/generate/listing/content', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
    .then(response => response.data)
    .catch(error => {
      const status = error?.response?.status;
      const data = error?.response?.data || {};
      const message =
        data?.message ||
        (status === 413 ? 'Payload too large' : error?.message) ||
        'Unknown error';
      // Preserve status and normalized message so UI can map properly
      throw { ...data, status, message };
    });
};

export const getComplementaryItems = body => {
  return axiosMicroService.post('/generate/listing/complementary-items', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

const STREAM_GLOBAL_TIMEOUT_MS = 45_000;
const STREAM_STALL_TIMEOUT_MS  = 20_000;

export const streamInstructionalVideos = (body, { onStage, onComplete, onError, signal } = {}) => {
  const rawLimit = Number(body?.limit);
  const limit = Number.isFinite(rawLimit) ? Math.max(1, Math.min(3, rawLimit)) : 3;
  const payload = {
    title: body?.title || '',
    ...(body?.imageUrl      ? { imageUrl:      body.imageUrl }      : {}),
    ...(body?.category      ? { category:      body.category }      : {}),
    ...(body?.queryOverride ? { queryOverride: body.queryOverride } : {}),
    limit,
  };

  const internalController = new AbortController();
  const onCallerAbort = () => internalController.abort();
  signal?.addEventListener('abort', onCallerAbort);

  let timedOut  = false;
  let stallTimer = null;

  const globalTimer = setTimeout(() => {
    timedOut = true;
    internalController.abort();
  }, STREAM_GLOBAL_TIMEOUT_MS);

  const cleanup = () => {
    clearTimeout(globalTimer);
    clearTimeout(stallTimer);
    signal?.removeEventListener('abort', onCallerAbort);
  };

  const resetStall = () => {
    clearTimeout(stallTimer);
    stallTimer = setTimeout(() => {
      timedOut = true;
      internalController.abort();
    }, STREAM_STALL_TIMEOUT_MS);
  };

  fetch(`${microServiceBaseURL}/instructional-videos/search/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
    signal: internalController.signal,
  })
    .then(async res => {
      if (!res.ok) {
        cleanup();
        onError?.({
          errorCode: res.status === 429 ? 'rate_limit' : 'network_error',
          message: `Request failed (${res.status})`,
        });
        return;
      }
      if (!res.body) {
        cleanup();
        onError?.({ errorCode: 'network_error', message: 'No response body' });
        return;
      }

      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      let completed = false;

      resetStall();
      try {
        outer: while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          resetStall();

          buf += dec.decode(value, { stream: true });
          const blocks = buf.split('\n\n');
          buf = blocks.pop();

          for (const block of blocks) {
            let eventType = 'message';
            let dataStr   = '';
            for (const line of block.split('\n')) {
              if      (line.startsWith('event: ')) eventType = line.slice(7).trim();
              else if (line.startsWith('data: '))  dataStr   = line.slice(6).trim();
            }
            if (!dataStr) continue;
            try {
              const data = JSON.parse(dataStr);
              if (eventType === 'stage') {
                onStage?.(data);
              } else if (eventType === 'complete') {
                onComplete?.(data);
                completed = true;
                break outer;
              } else if (eventType === 'error') {
                onError?.(data);
                completed = true;
                break outer;
              }
            } catch { /* malformed JSON block — skip */ }
          }
        }

        if (!completed) {
          onError?.({ errorCode: 'incomplete_stream', message: 'Stream closed unexpectedly. Please try again.' });
        }
      } finally {
        cleanup();
      }
    })
    .catch(err => {
      cleanup();
      if (timedOut) {
        onError?.({ errorCode: 'timeout', message: 'The search took too long. Please try again.' });
      } else if (err?.name !== 'AbortError') {
        onError?.({ errorCode: 'network_error', message: err?.message || 'Request failed' });
      }
    });
};

export const enhanceField = (body) => {
  return axiosMicroService.post('/generate/listing/enhance-field', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

export const generateAIImages = body => {
  return axiosMicroService.post('/generate/listing/images', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

export const getCredits = (token) => {
  return axiosMicroService.get('/ai/credits', { params: { listingToken: token } })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

// Translation API
/**
 * Translate multiple texts to multiple target languages
 * @param {Object} params - Translation parameters
 * @param {Array<string>} params.texts - Array of texts to translate
 * @param {string} params.source - Source language code (e.g., 'en')
 * @param {Array<string>} params.targets - Target language codes (e.g., ['fr', 'es', 'zh'])
 * @returns {Promise<Object>} Response with translations object: { success: true, translations: { [locale]: [{ input, output }] } }
 */
export const translateTextsMulti = async ({ texts, source, targets }) => {
  try {
    const response = await axiosMicroService.post('/translate/texts-multi', {
      texts,
      source,
      targets
    });

    return response.data; // Returns { success: true, translations: {...} }
  } catch (error) {
    console.error('Translation API error:', error);

    // Return error in expected format
    throw {
      success: false,
      error: error.response?.data?.error || error.message || 'Translation failed',
      status: error.response?.status || 500
    };
  }
};

// Blog Generation API's
// Blog titles with labels
export const generateBlogTitleswithLabels = body => {
  return axiosMicroService.post('/generate/blog/titles', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

// Store blog titles with labels
export const generateStoreBlogTitleswithLabels = body => {
  return axiosMicroService.post('/generate/store/blog/titles', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

export const generateBlogs = body => {
  return axiosMicroService.post('/generate/blog/content', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

export const generateStoreBlogs = body => {
  return axiosMicroService.post('/generate/store/blog/content', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};


// Blog CRUD APIs
export const createBlogPost = body => {
  return axiosMicroService.post('/blogs', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
}

export const getBlogsByListingId = listingId => {
  return axiosMicroService.get(`/blogs/listing/${listingId}`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

export const updateBlogPost = (id, update) => {
  return axiosMicroService.put(`/blogs/${id}`, { ...update })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

export const retryBlogTranslations = id => {
  return axiosMicroService.post(`/blogs/${id}/translations/retry`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
}

export const updateStoreBlogsPost = (id, update) => {
  return axiosMicroService.put(`/store-blogs/${id}`, { ...update })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

export const deleteBlogPost = id => {
  return axiosMicroService.delete(`/blogs/${id}`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

// delete store blog
export const deleteStoreBlogPostApi = id => {
  return axiosMicroService.delete(`/store-blogs/${id}`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
};

export const publishBlogPost = id => {
  return axiosMicroService.put(`/blogs/${id}`, { id, status: 'published' })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
}

export const createStoreBlogPost = body => {
  return axiosMicroService.post('/store-blogs', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
}

export const retryStoreBlogTranslations = id => {
  return axiosMicroService.post(`/store-blogs/${id}/translations/retry`)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
}

// publish store blogs
export const publishStoreBlogPost = id => {
  return axiosMicroService.put(`/store-blogs/${id}`, { id, status: 'published' })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
}

// create store blog
export const createStoreBlogPostApi = body => {
  return axiosMicroService.post('/store-blogs/custom', body)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
}

// Fetch a single blog by its ID
export const getBlogPost = id => {
  return axiosMicroService.get(`/blogs/${id}`, { timeout: 10000 })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error.response?.data ?? error;
    });
};

export const getStoreBlogPost = id => {
  return axiosMicroService.get(`/store-blogs/${id}`, { timeout: 10000 })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error.response?.data ?? error;
    });
};

export const getStoreBlogByUserIdAndSlug = (userId, slug) => {
  return post('/api/store-blogs/user-slug', { userId, slug });
};

// upload file to backend
export const uploadImageGetURL = formData => {
  return axiosMicroService.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
    .then(response => {
      return response.data;
    })
    .catch(error => {
      throw error?.response?.data || error;
    });
}

/**
 * Generates SEO tags for a Showcase Gallery image or video via OpenAI Vision.
 * Used exclusively by MediaLibraryPage — not for listing images or store photos.
 * @param {{ imageUrl: string, storeName: string, city: string, mediaType: 'image'|'video' }} body
 * @returns {Promise<{ success: boolean, data: { fileName, altText, titleTag, caption } }>}
 */
export const generateGalleryImageSeoTags = body =>
  axiosMicroService
    .post('/generate/image/seo-tags', body)
    .then(r => r.data)
    .catch(err => { throw err.response?.data || err; });

// Fetch all assets (footer, topbar, and optionally page data) in a single request
export const getAllAssets = (lang = 'en', pageId = null, language = null) => {
  const params = { lang };
  if (pageId && language) {
    params.pageId = pageId;
    params.language = language;
  }

  const apiBase = apiFetchBaseUrl();
  const url = `${apiBase}/api/assets`;

  return axios.get(url, {
    params,
    headers: internalApiHeaders(),
    withCredentials: false,
  }).then(response => response.data);
};

// Fetch specific asset by type
export const getAssetByType = (type, lang = 'en', pageId = null, language = null) => {
  const params = { lang };
  if (pageId && language) {
    params.pageId = pageId;
    params.language = language;
  }

  const apiBase = apiFetchBaseUrl();
  const url = `${apiBase}/api/assets/${type}`;

  return axios.get(url, {
    params,
    headers: internalApiHeaders(),
    withCredentials: false,
  }).then(response => response.data);
};

// Retry profile translations for the authenticated user
export const retryProfileTranslations = userId => {
  const apiBase = apiBaseUrl();
  const url = `${apiBase}/api/retry-profile-translations`;
  return axios.post(url, { userId }, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).then(response => response.data);
};

// Legacy functions for backward compatibility
export const getFooter = (lang = 'en') => {
  return getAssetByType('footer', lang);
};

export const getTopbar = (lang = 'en') => {
  return getAssetByType('topbar', lang);
};

export const getPageDatabyIdandLanguage = (pageId, language) => {
  return getAssetByType('page', 'en', pageId, language);
};

export const requestStoreCategorySync = () => {
  return post('/api/store-categories/request-sync', {});
};

export const getStoreCategorySyncStatus = () => {
  return get('/api/store-categories/sync-status');
};

export const getStoreCategoryCounts = () => {
  return post('/api/store-categories/counts', {});
};

/** Authenticated: category tree + groups from Mongo (or legacy publicData fallback on server). */
export const getStoreCategoryConfig = () => {
  return get('/api/store-categories/config');
};

/** Public: category config for a store owner by Sharetribe user UUID. */
export const getStoreCategoryPublicConfig = userId => {
  const q = encodeURIComponent(userId);
  return get(`/api/store-categories/public-config?userId=${q}`);
};

/**
 * Single save: profile + all listing assignments in one request. Uses long timeout (e.g. 90s).
 */
export const saveStoreCategories = (payload, timeoutMs = 90000) => {
  return post('/api/store-categories/save', payload, { timeoutMs });
};

/**
 * Save a single store category (create or edit) without listing assignments.
 * Request body: { type: 'create' | 'edit', category, baseLocale }.
 */
export const saveStoreCategory = (payload, timeoutMs = 90000) => {
  return post('/api/store-categories/save-category', payload, { timeoutMs });
};

/**
 * Delta save: send only changes (orderedCategoryIds, deletedCategoryIds, visibilityChanges, etc.). Optional newCategories. No listing assignments.
 */
export const saveStoreCategoriesDelta = (payload, timeoutMs = 90000) => {
  return post('/api/store-categories/save-delta', payload, { timeoutMs });
};

/**
 * Batch assign/unassign listings to a store category.
 * Overrides the default 15s timeout with 30s because large batches can take longer.
 * Client should chunk if assignListingIds.length + unassignListingIds.length > ASSIGN_LISTINGS_MAX_BATCH_SIZE.
 */
export const assignListings = (payload, timeoutMs = 30_000) => {
  return post('/api/store-categories/assign-listings', payload, { timeoutMs });
};

/**
 * Sync a single listing's storeCategory.lvl* hierarchy strings across all 4 locale Algolia
 * indices immediately after a listing edit-form save.
 * Without this, only the current-UI-language index is updated by createAlgoliaData;
 * FR/ES/ZH indices would stay stale until the 30-second listing-events cron runs.
 */
export const syncListingAlgolia = (listingId) => {
  return post('/api/store-categories/sync-listing', { listingId });
};

// RAS-2589 — bulk "update all my listings to this address" (background job).
// Returns 202 immediately; poll getListingsAddressUpdateStatus for progress.
export const updateAllListingsAddress = (body) => {
  return post('/api/listings/update-all-addresses', body);
};

export const getListingsAddressUpdateStatus = () => {
  return get('/api/listings/address-update-status');
};

// RAS-2590 — bulk "apply my minimum owner delivery/pickup rates to all my
// listings" (background job). Returns 202 immediately; poll
// getListingsMinRateUpdateStatus for progress.
export const updateAllListingsMinRates = (body) => {
  return post('/api/listings/update-all-min-rates', body);
};

export const getListingsMinRateUpdateStatus = () => {
  return get('/api/listings/min-rate-update-status');
};

// Notifications Twilio API
export const sendSMSNotification = (body) => {
  return post('/api/send-sms-notification', body);
};

// /check-user-stripe-account-linked
export const onCheckUserStripeAccountLinked = (body) => {
  return post('/api/check-user-stripe-account-linked', body);
};

// Free Transaction API's
export const freeTransactionCreate = body => {
  return post('/api/free-transaction-create', body);
};

export const fetchFreeTransactions = body => {
  return post('/api/free-transaction-find', body);
};

export const createStripeTransferForProvider = body => {
  return post('/api/create-transfer', body);
};

export const getAdminConfigApi = () => {
  return get('/api/admin/config');
};
export const getAdminPersonaDetailsApi = () => {
  return get('/api/admin/persona-details');
};
export const updateAdminDefaultConfigApi = (body) => {
  return post('/api/admin/update-default-config', body);
};
export const getPersonaUserDetailsApi = (body) => {
  return post('/api/admin/persona-user-details', body);
};
export const isTransactionNeedingVerificationApi = (body) => {
  return post('/api/update-transaction-verification-status', body);
};
export const sendMessageNotificationToOtherPartyApi = (body) => {
  return post('/api/send-message-notification-to-other-party', body);
};

export const updateAdminConfigAnalyticsDataApi = (body) => {
  return post('/api/admin/update-analytics-data', body);
};
export const isUsersEmailVerifiedApi = (body) => {
  return post('/api/is-users-email-verified', body);
};
export const isUsersEmailVerifiedBatchApi = (body) => {
  return post('/api/is-users-email-verified-batch', body);
};
export const sendEmailToSupportApi = (body) => {
  return post('/api/send-email-to-support', body);
};

// Store Blog CRUD APIs
// export const createStoreBlogPost = body => {
//   return post('/api/store-blogs', body);
// };

// How long a store blogs read may take before the caller stops waiting (ticket 91).
//
// These two are fetched while a store page is being built, and when the server renders that
// page it fetches them from itself over the public address. Neither had a time limit, so one
// slow read held the whole page until the platform cut it off at 30 seconds and the visitor
// got an error page instead of a store.
//
// Above the server's own two-second database limit, so a slow read is answered by our own
// error rather than by this giving up first, and far below the platform's 30, so a store page
// renders without its blogs rather than not at all.
//
// The number is the worst case a visitor waits. The reads that hang do not reach a slow query,
// so the database limit never fires on them and this is the only limit that bites: 8000 here
// would have reproduced exactly the 8-second store page the ticket is about. Healthy reads
// take about 220 ms, so 3500 leaves fifteen times the headroom they need.
const STORE_BLOG_TIMEOUT_MS = 3500;

// The store page asks for `view: 'storefront'`: card fields only, no post bodies, visible posts
// only (server/api/storeBlogController.js). The owner's blog dashboard edits from the full
// documents, so it calls without a view.
export const STORE_BLOG_STOREFRONT_VIEW = 'storefront';

export const getStoreBlogsByUserId = (userId, options = {}) => {
  return post(
    '/api/store-blogs/user',
    { userId, ...options },
    { timeoutMs: STORE_BLOG_TIMEOUT_MS }
  );
};

export const getListingBlogsByUserId = (userId, options = {}) => {
  return post(
    '/api/store-blogs/user/listing',
    { userId, ...options },
    { timeoutMs: STORE_BLOG_TIMEOUT_MS }
  );
};

export const updateStoreBlogPost = (id, update) => {
  return post('/api/store-blogs/update', { id, ...update });
};

export const deleteStoreBlogPost = id => {
  return post('/api/store-blogs/delete', { id });
};

// Quote APIs
export const createQuoteApi = body => post('/api/quotes', body);
export const fetchQuoteApi = quoteId => get(`/api/quotes/${quoteId}`);
